import { UNIT_PLACEHOLDER } from "Utilities/Address/AddressValidation/providers/smarty/smartyAutocomplete";

import {
  abbreviationAwarePrefixMatch,
  deriveShadowSuffix,
  streetExtendsPastLine,
} from "./streetMatching";

// Scores a candidate against the city/state/zip the user already committed (addr1 is excluded —
// it's the prefix filter): +2 exact, +1 prefix either way, 0 otherwise; empty values don't bias.
function candidateConsistencyScore(candidate, city, state, zip) {
  const committedFields = [
    ["city", city],
    ["state", state],
    ["zip", zip],
  ];
  return committedFields.reduce((totalScore, [fieldName, committedValue]) => {
    const normalizedCommitted = (committedValue || "").trim().toLowerCase();
    if (!normalizedCommitted) return totalScore;
    const normalizedCandidate = (candidate[fieldName] || "").toLowerCase();
    if (!normalizedCandidate) return totalScore;
    if (normalizedCandidate === normalizedCommitted) return totalScore + 2;
    if (
      normalizedCandidate.startsWith(normalizedCommitted) ||
      normalizedCommitted.startsWith(normalizedCandidate)
    ) {
      return totalScore + 1;
    }
    return totalScore;
  }, 0);
}

// Is a candidate's subfield value *consistent* with the form — does it complete/match the value
// rather than replace it? Consistent when either side is empty, they match case-insensitively, or
// one is a ghostable prefix of the other (a completion either direction). Inconsistent only when
// the candidate would genuinely overwrite a non-empty typed value ("Boston" vs "Springfield").
// Shared by the group-blur consistency gate and the live clear-stash-restore effect.
export function valueConsistent(committed, candidateValue) {
  const a = (committed || "").trim();
  const b = (candidateValue || "").trim();
  if (!a || !b) return true;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  return deriveShadowSuffix(a, b) !== "" || deriveShadowSuffix(b, a) !== "";
}

// Is `candidate` a bare-building shell that must not be presented (ghosted, picked, or
// committed)? Smarty returns a multi-unit building's bare street as its own secondary-less row
// alongside its Fl/Ste/Unit groups; picking it commits an address the frontend marks verified but
// the backend's deliverability check rejects (missing required secondary → USPSPartialMatch).
// Discriminator is structural: a same-building sibling carrying a real unit group (entries > 1),
// where "same building" = same city + the bare street is a prefix of the sibling's street.
// ZIP is deliberately NOT in the key — one building spans several USPS ZIPs, so a shell and its
// unit group can match on city + street yet differ by ZIP and still be one building. A standalone
// house (entries 0, no unit sibling) and a genuine leaf are not shells and stay.
export function isBareBuildingShell(candidate, candidates) {
  if (candidate.entries > 1) return false; // a unit group is never the shell
  const street = (candidate.addr1 || "").toLowerCase();
  if (!street) return false;
  return candidates.some(
    (other) =>
      other !== candidate &&
      other.entries > 1 &&
      (other.city || "").toLowerCase() ===
        (candidate.city || "").toLowerCase() &&
      (other.addr1 || "").toLowerCase().startsWith(`${street} `)
  );
}

// Does addr1 carry the unit-placeholder sentinel? Bare-building paths key off this (not
// `entries`) so they line up with what's actually ghosted.
export function addressNeedsUnit(address) {
  return (
    typeof address?.addr1 === "string" &&
    address.addr1.includes(UNIT_PLACEHOLDER)
  );
}

// Guards city/state/zip against a candidate that contradicts the user (the street already matched
// via the prefix filter); a contradiction leaves typed values standing as unverifiable. Only a
// TYPED value (editedFields) can contradict — a stale prefilled subfield the candidate disagrees
// with isn't blocking (live ghosting replaces it), else retyping a street over an old address
// whose stale city hasn't cleared yet would wrongly settle a verified leaf unverifiable.
export function candidateConsistentWithForm(
  candidate,
  value,
  editedFields = {}
) {
  return ["city", "state", "zip"].every(
    (field) =>
      !editedFields[field] || valueConsistent(value[field], candidate[field])
  );
}

// Pick the candidate that best fits what the user typed: filter to addr1 prefix matches, then
// prefer the one whose committed city/state/zip are most consistent, first-match on ties.
export function pickTopCandidate({
  typed,
  allCandidates,
  retainedCandidates,
  committedCity,
  committedState,
  committedZip,
}) {
  // Forward matches — what the user typed is an (abbreviation-aware) prefix
  // of the candidate — come from the live results and drive normal
  // completion + verification.
  if (allCandidates.length) {
    const prefixMatches = typed
      ? allCandidates.filter(
          (candidate) =>
            candidate.addr1 &&
            abbreviationAwarePrefixMatch(typed, candidate.addr1)
        )
      : allCandidates;
    if (prefixMatches.length) {
      // Drop bare-building shells from the scoring pool (see isBareBuildingShell) so a real unit
      // GROUP wins instead — its placeholder ghosts and accepting it routes into guided unit-fill,
      // never committing the shell. A shell is only flagged when its same-building unit sibling is
      // in the pool, so `deliverable` is non-empty whenever a shell was dropped; the fallback only
      // guards the impossible all-shell case so `reduce` never sees an empty array.
      const deliverable = prefixMatches.filter(
        (candidate) => !isBareBuildingShell(candidate, prefixMatches)
      );
      const scorePool = deliverable.length ? deliverable : prefixMatches;
      const scored = scorePool.map((candidate) => ({
        candidate,
        score: candidateConsistencyScore(
          candidate,
          committedCity,
          committedState,
          committedZip
        ),
      }));
      // Stage 1 — which building: `bestScored` is the first row at the best consistency score.
      // Smarty geo-ranks buildings (IP-based) and the score layers committed city/state/zip on
      // top, so keeping the first on ties preserves Smarty's geo order.
      const bestScored = scored.reduce((best, current) =>
        current.score > best.score ? current : best
      );
      // Stage 2 — which unit type: prefer the MOST-POPULATED designator, not Smarty's order.
      // Smarty lists a building's groups (Fl/Lbby/Ste/Unit) ALPHABETICALLY, so the first is
      // systematically not dominant (an office tower leads with "Fl" when tenants are mostly
      // "Ste"); `entries` counts secondaries per designator, so max-entries is the likeliest pick.
      // Scoped to bestScored's city + score so prevalence decides only the TYPE, never overriding
      // geo across cities. A wrong guess costs one keystroke (type the designator → re-fetch narrows).
      const buildingCity = (bestScored.candidate.city || "").toLowerCase();
      const sameBuilding = scored.filter(
        (scoredItem) =>
          scoredItem.score === bestScored.score &&
          (scoredItem.candidate.city || "").toLowerCase() === buildingCity
      );
      const dominant = sameBuilding.reduce((best, current) =>
        (current.candidate.entries || 0) > (best.candidate.entries || 0)
          ? current
          : best
      );
      return { topCandidate: dominant.candidate, appendedTail: "" };
    }
  }
  // No forward match — the user may have appended a unit past a street Smarty recognized (live
  // query returns nothing). Fall back to retained candidates and find a BASE: a candidate whose
  // addr1 is a token-boundary prefix of what's typed. Its locality keeps autocompleting; the
  // remainder is the appended unit, kept verbatim, forcing the unverifiable verdict.
  if (!typed) return { topCandidate: null, appendedTail: "" };
  const pool = allCandidates.length ? allCandidates : retainedCandidates;
  // Exact match against a recently-matched candidate (the group-blur case): the per-field commit
  // already canonicalized addr1 to a candidate's value, and the live re-fetch may lag, so the
  // retained candidate verifies without waiting on the round-trip. Bidirectional prefix match =
  // equivalence (abbreviation-aware); shells excluded (never verifiable).
  const exactMatch = pool.find(
    (candidate) =>
      candidate.addr1 &&
      !isBareBuildingShell(candidate, pool) &&
      abbreviationAwarePrefixMatch(typed, candidate.addr1) &&
      abbreviationAwarePrefixMatch(candidate.addr1, typed)
  );
  if (exactMatch) return { topCandidate: exactMatch, appendedTail: "" };
  let base = null;
  let baseTail = "";
  pool.forEach((candidate) => {
    if (!candidate.addr1) return;
    // The candidate's street must be a token-boundary prefix of what's typed,
    // ABBREVIATION-AWARE in both spellings: Smarty normalizes the suffix ("St"),
    // the user may spell it out ("street"). A character-level prefix check
    // misfires here — "St" is a literal prefix of "street", so it would match
    // mid-token and reject a real base (the locality ghost dies the instant a
    // unit is appended). `streetExtendsPastLine` token-walks with
    // `tokensEquivalent`, so it aligns "street"≈"St" and still requires at least
    // one further (unit) token past the street. Keep the longest base.
    if (!streetExtendsPastLine(typed, candidate.addr1)) return;
    if (base && candidate.addr1.length <= base.addr1.length) return;
    base = candidate;
    // The appended tail is the typed tokens past the candidate's street tokens
    // (the tail aligns to the end), kept in the user's spelling.
    const baseTokenCount = candidate.addr1.trim().split(/\s+/).filter(Boolean)
      .length;
    baseTail = typed.trim().split(/\s+/).filter(Boolean).slice(baseTokenCount)
      .join(" ");
  });
  return { topCandidate: base, appendedTail: base ? baseTail : "" };
}
